import React from 'react';
import { Space, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faCopy, faStar, faTrash, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../../../context/ThemeContext';
import Table from '../../../../commonComponents/Table';
import { ViewProps } from '../types.d';
import { TEMPLATE_TABLE_CONSTANTS } from '../constants';
import { getTemplateTypeLabel, getStatusColor } from '../../constants/emailConstants';
import { formatDateAndTime } from '../../../../utils/formatDateTime';
import { TemplateNameCell, TemplateInfo, TemplateName, TemplateDescription, ActionIconButton } from '../styles';

const DesktopView: React.FC<ViewProps> = ({
  templates,
  templateTypes,
  onEdit,
  onDuplicate,
  onSetDefault,
  onDelete,
  onPreview,
  onTestEmail,
  loading = false,
}) => {
  const { isDarkMode } = useTheme();

  const columns: ColumnsType<any> = [
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.TEMPLATE_NAME,
      key: 'name',
      fixed: 'left',
      width: 250,
      render: (_, template) => (
        <TemplateNameCell>
          <TemplateInfo>
            <TemplateName>{template.name}</TemplateName>
            {template.description && (
              <TemplateDescription>{template.description}</TemplateDescription>
            )}
          </TemplateInfo>
        </TemplateNameCell>
      ),
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.TYPE,
      key: 'type',
      width: 120,
      render: (_, template) => (
        <Tag color="blue">{getTemplateTypeLabel(template.type, templateTypes)}</Tag>
      ),
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.STATUS,
      key: 'status',
      width: 120,
      render: (_, template) => (
        <Tag color={getStatusColor(template.status)}>
          {template.status.charAt(0).toUpperCase() + template.status.slice(1)}
        </Tag>
      ),
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.DEFAULT,
      key: 'isDefault',
      width: 100,
      render: (_, template) =>
        template.isDefault ? (
          <Tag color="green">
            Default
          </Tag>
        ) : (
          '-'
        ),
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.USAGE_COUNT,
      key: 'usageCount',
      width: 100,
      render: (_, template) => template.usageCount || 0,
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.LAST_USED,
      key: 'lastUsed',
      width: 150,
      render: (_, template) =>
        template.lastUsed ? formatDateAndTime(template.lastUsed).formattedDate : TEMPLATE_TABLE_CONSTANTS.MESSAGES.NEVER,
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.CREATED,
      key: 'createdAt',
      width: 200,
      render: (_, template) => (
        <div>
          <div>{formatDateAndTime(template.createdAt).formattedDate}</div>
          {template.createdBy && (
            <div style={{ fontSize: '12px', opacity: 0.6 }}>
              {TEMPLATE_TABLE_CONSTANTS.MESSAGES.BY_PREFIX} {template.createdBy}
            </div>
          )}
          {template.updatedAt && template.updatedAt !== template.createdAt && (
            <div style={{ fontSize: '12px', opacity: 0.6 }}>
              {TEMPLATE_TABLE_CONSTANTS.MESSAGES.UPDATED_PREFIX} {formatDateAndTime(template.updatedAt).formattedDate}
              {template.updatedBy && ` ${TEMPLATE_TABLE_CONSTANTS.MESSAGES.BY_PREFIX} ${template.updatedBy}`}
            </div>
          )}
        </div>
      ),
    },
    {
      title: TEMPLATE_TABLE_CONSTANTS.LABELS.ACTIONS,
      key: 'actions',
      width: 180,
      align: 'center' as const,
      render: (_, template) => (
        <Space size="small">
          {template.htmlContent && (
            <ActionIconButton onClick={() => onPreview(template)} iconColor="#2D6CCA" title="Preview">
              <FontAwesomeIcon icon={faEye} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          )}
          <ActionIconButton onClick={() => onEdit(template)} iconColor="#2D6CCA">
            <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
          </ActionIconButton>
          <ActionIconButton onClick={() => onDuplicate(template)} iconColor="#2D6CCA">
            <FontAwesomeIcon icon={faCopy} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
          </ActionIconButton>
          {onTestEmail && (
            <ActionIconButton onClick={() => onTestEmail(template)} iconColor="#2D6CCA" title="Send test email">
              <FontAwesomeIcon icon={faPaperPlane} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          )}
          {!template.isDefault && (
            <ActionIconButton onClick={() => onSetDefault(template)} iconColor="#2D6CCA">
              <FontAwesomeIcon icon={faStar} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          )}
          {!template.isDefault && (
            <ActionIconButton onClick={() => onDelete(template)} iconColor="#f5222d">
              <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={templates}
      rowKey="id"
      pagination={false}
      loading={loading}
      emptyText={TEMPLATE_TABLE_CONSTANTS.MESSAGES.NO_TEMPLATES}
      scroll={{ x: true, y: 600 }}
      headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
      removeVerticalBorders={true}
      thPadding="15px 10px"
      tdPadding="10px 10px"
      firstChildBorderRadius="8px"
      border="0"
      borderBottomLeftRight={true}
      theme={isDarkMode ? 'dark' : 'light'}
    />
  );
};

export default DesktopView;
